import { TMessageKey } from "@/i18n/types.gen";
import { MatchedRoute, Route } from "./router";

export type DCRouteData = {
  allowAnonymous?: boolean;
  allowShareholder?: boolean;
  title?: TMessageKey;
};

export const boardMemberRoutes = [
  {
    name: "board-member/dashboard",
    path: "/board-member/:orgNo",
    data: { title: "common.dashboard" },
  },
  {
    name: "board-member/articlesofassociation",
    path: "/board-member/:orgNo/articlesofassociation",
    data: { title: "header.articles-of-association" },
  },
  {
    name: "board-member/meeting",
    path: "/board-member/:orgNo/meetings/project/:projectId",
    data: { title: "meetings.board-meeting" },
  },
  {
    name: "board-member/messaging",
    path: "/board-member/:orgNo/messaging",
    data: { title: "header.recipient.messaging" },
  },
  {
    name: "board-member/archive",
    path: "/board-member/:orgNo/archive",
    data: { title: "header.archive" },
  },
  {
    name: "board-member/schedule",
    path: "/board-member/:orgNo/schedule/:view?/:year?/:month?",
    data: { title: "header.schedule" },
  },
] as const satisfies Route<DCRouteData>[];

export const shareholderRoutes = [
  {
    name: "shareholder/dashboard-redirect",
    path: "/shareholder/:orgNo/:shareholderId",
    redirectTo: "/shareholder/:orgNo/:shareholderId/dashboard",
    data: { allowShareholder: true },
  },
  {
    name: "shareholder/dashboard",
    path: "/shareholder/:orgNo/:shareholderId/dashboard",
    data: { allowShareholder: true, title: "common.dashboard" },
  },
  {
    name: "shareholder/articlesofassociation",
    path: "/shareholder/:orgNo/:shareholderId/articlesofassociation",
    data: { allowShareholder: true, title: "header.articles-of-association" },
  },
  {
    name: "shareholder/archive",
    path: "/shareholder/:orgNo/:shareholderId/archive",
    data: { allowShareholder: true, title: "header.shareholderview.archive" },
  },
  {
    name: "shareholder/messaging",
    path: "/shareholder/:orgNo/:shareholderId/messaging/:id?",
    data: { allowShareholder: true, title: "header.recipient.messaging" },
  },
  {
    name: "shareholder/schedule",
    path: "/shareholder/:orgNo/:shareholderId/schedule/:view?/:year?/:month?",
    data: { allowShareholder: true, title: "header.meetings" },
  },
  {
    name: "shareholder/details",
    path: "/shareholder/:orgNo/:shareholderId/:tab",
    data: { allowShareholder: true, title: "header.company" },
  },
] as const satisfies Route<DCRouteData>[];

export const adminRoutes = [
  {
    name: "admin/root",
    path: "/company/:orgNo",
    data: { title: "header.company" },
  },
  {
    name: "admin/dashboard",
    path: "/company/:orgNo/dashboard",
    data: { title: "common.dashboard" },
  },
  {
    name: "admin/follow-up",
    path: "/company/:orgNo/follow-up",
    redirectTo: "/company/:orgNo/follow-up/signatures",
  },
  {
    name: "admin/entity-data",
    path: "/company/:orgNo/entity-data",
    data: { title: "header.entity-data" },
  },
  {
    name: "admin/company-dashboard",
    path: "/company/:orgNo/dashboards/:dashboardId",
    data: { title: "common.dashboard" },
  },
  {
    name: "admin/follow-up/matters",
    path: "/company/:orgNo/follow-up/matters/:tab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "admin/follow-up/signatures",
    path: "/company/:orgNo/follow-up/signatures/:signaturesTab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "admin/follow-up/companyinfo",
    path: "/company/:orgNo/follow-up/company-info/:tab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "admin/follow-up/shareholders",
    path: "/company/:orgNo/follow-up/shareholders/:tab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "admin/search",
    path: "/company/:orgNo/search",
    data: { title: "search" },
  },
  {
    name: "admin/articlesofassociation",
    path: "/company/:orgNo/articlesofassociation",
    data: { title: "header.articles-of-association" },
  },
  {
    name: "admin/edit-articlesofassociation",
    path: "/company/:orgNo/articlesofassociation/edit",
    data: { title: "articlesOfAssociation.edit-articlesofassociation" },
  },
  {
    name: "admin/power-of-attorney",
    path: "/company/:orgNo/power-of-attorney/:powerOfAttorneyTab?",
    data: { title: "header.poa" },
  },
  {
    name: "admin/power-of-attorney-details",
    path: "/company/:orgNo/power-of-attorney/:powerOfAttorneyId/details",
    data: { title: "header.poa" },
  },
  {
    name: "admin/ownership-redirect",
    path: "/company/:orgNo/ownership",
    redirectTo: "/company/:orgNo/ownership/owns/list",
  },
  {
    name: "admin/ownership",
    path: "/company/:orgNo/ownership/:ownershipGraphType/:ownershipTab",
    data: { title: "header.ownership" },
  },
  {
    name: "admin/roles",
    path: "/company/:orgNo/roles/:rolesTab?",
    data: { title: "companyRoles.header" },
  },
  {
    name: "admin/role-details",
    path: "/company/:orgNo/role/:id/details",
    data: { title: "companyRoles.header" },
  },
  {
    name: "admin/settings",
    path: "/company/:orgNo/settings/:menuItem?/:roleTb?",
    data: { title: "settings" },
  },
  {
    name: "admin/shareholders-dashboard",
    path: "/company/:orgNo/shareholders/dashboard",
    data: { title: "header.shareholderBook" },
  },
  {
    name: "admin/shareholders-redirect",
    path: "/company/:orgNo/shareholders",
    redirectTo: "/company/:orgNo/shareholders/list",
  },
  {
    name: "admin/shareholders-list",
    path: "/company/:orgNo/shareholders/list",
    data: { title: "header.shareholders" },
  },
  {
    name: "admin/shareholders-edit",
    path: "/company/:orgNo/shareholders/edit",
    data: { title: "portfolio.links.edit-shareholder" },
  },
  {
    name: "admin/partnership-register",
    path: "/company/:orgNo/partners",
    data: { title: "partnership-register.partners" },
  },
  {
    name: "admin/partners-per-date",
    path: "/company/:orgNo/partners/per-date",
    data: { title: "partnership-register.per-date.title" },
  },
  {
    name: "admin/partners-transactions",
    path: "/company/:orgNo/partners/transactions",
    data: { title: "header.transactions" },
  },
  {
    name: "admin/partners-transaction-details",
    path: "/company/:orgNo/partners/transactions/:id",
    data: { title: "partnership-register.transactions.details" },
  },
  {
    name: "admin/partners-transaction-edit",
    path: "/company/:orgNo/partners/transactions/:id/edit",
    data: { title: "partnership-register.per-date.title" },
  },
  {
    name: "admin/partner-details-redirect",
    path: "/company/:orgNo/partners/:id",
    redirectTo: "/company/:orgNo/partners/:id/overview",
  },
  {
    name: "admin/partner-details",
    path: "/company/:orgNo/partners/:id/:tab",
    data: { title: "partnership-register.partners" },
  },
  {
    name: "admin/shareholder-details-redirect",
    path: "/company/:orgNo/shareholders/list/:id",
    redirectTo: "/company/:orgNo/shareholders/list/:id/overview",
  },
  {
    name: "admin/shareholder-details",
    path: "/company/:orgNo/shareholders/list/:id/:tab",
    data: { title: "shareholder" },
  },
  {
    name: "admin/import-shareholders",
    path: "/company/:orgNo/shareholders/import",
    data: { title: "shareholderImport.importShareholders" },
  },
  {
    name: "admin/share-classes",
    path: "/company/:orgNo/shareholders/classes",
    data: { title: "header.stock-classes" },
  },
  {
    name: "admin/create-share-class",
    path: "/company/:orgNo/shareholders/classes/create",
    data: { title: "shareClasses.shareClassForm.create" },
  },
  {
    name: "admin/edit-share-class",
    path: "/company/:orgNo/shareholders/classes/:shareTypeId",
    data: { title: "shareClasses.shareClassForm.edit" },
  },
  {
    name: "admin/cap-table",
    path: "/company/:orgNo/instruments/cap-table",
    data: { title: "cap-table.title" },
  },
  {
    name: "admin/cap-table-scenario",
    path: "/company/:orgNo/instruments/cap-table/scenarios/:scenarioId",
    data: { title: "cap-table.scenario.title" },
  },
  {
    name: "admin/financial-instruments",
    path: "/company/:orgNo/instruments",
    redirectTo: "/company/:orgNo/instruments/options/holders",
  },
  {
    name: "admin/instrument-options",
    path: "/company/:orgNo/instruments/options",
    redirectTo: "/company/:orgNo/instruments/options/holders",
  },
  {
    name: "admin/instrument-option-holders",
    path: "/company/:orgNo/instruments/options/holders",
    data: { title: "options.holders-title" },
  },
  {
    name: "admin/instrument-option-programs",
    path: "/company/:orgNo/instruments/options/programs",
    data: { title: "options.programs-title" },
  },
  {
    name: "admin/instrument-option-program",
    path: "/company/:orgNo/instruments/options/programs/:planId",
    data: { title: "options.programs-title" },
  },
  {
    name: "admin/instrument-option-exercises",
    path: "/company/:orgNo/instruments/options/exercises",
    data: { title: "options.exercises-title" },
  },
  {
    name: "admin/instrument-convertible-loans",
    path: "/company/:orgNo/instruments/convertible-loans",
    data: { title: "header.financial-instruments" },
  },
  {
    name: "admin/instrument-warrants",
    path: "/company/:orgNo/instruments/warrants",
    data: { title: "header.financial-instruments" },
  },
  {
    name: "admin/financial-instrument-details",
    path: "/company/:orgNo/instruments/:instrumentId",
    data: { title: "header.financial-instruments" },
  },
  {
    name: "admin/contributions-overview",
    path: "/company/:orgNo/shareholders/contributions",
    data: { title: "header.shareholder-contributions" },
  },
  {
    name: "admin/contributions-log",
    path: "/company/:orgNo/shareholders/contributions/log",
    data: { title: "header.shareholder-contributions" },
  },
  {
    name: "admin/contribution-details",
    path: "/company/:orgNo/shareholders/contributions/log/:contributionId",
    data: { title: "header.shareholder-contributions" },
  },
  {
    name: "admin/register-contribution",
    path: "/company/:orgNo/shareholders/contributions/new/:contributionType",
    data: { title: "header.shareholder-contributions" },
  },
  {
    name: "admin/commitments-overview",
    path: "/company/:orgNo/shareholders/commitments",
    data: { title: "header.shareholder-commitments" },
  },
  {
    name: "admin/commitments-log",
    path: "/company/:orgNo/shareholders/commitments/log",
    data: { title: "header.shareholder-commitments" },
  },
  {
    name: "admin/commitment-deltails",
    path: "/company/:orgNo/shareholders/commitments/log/:commitmentId",
    data: { title: "header.shareholder-commitments" },
  },
  {
    name: "admin/register-commitment",
    path: "/company/:orgNo/shareholders/commitments/new/:commitmentType",
    data: { title: "header.shareholder-commitments" },
  },
  {
    name: "admin/transactions",
    path: "/company/:orgNo/shareholders/transactions",
    data: { title: "header.transactions" },
  },
  {
    name: "admin/new-transaction",
    path: "/company/:orgNo/shareholders/transactions/new/:transactionType",
    data: { title: "portfolio.links.new-transaction" },
  },
  {
    name: "admin/transaction-details",
    path: "/company/:orgNo/shareholders/transactions/:id",
    data: { title: "transactions.transaction-details" },
  },
  {
    name: "admin/edit-transaction",
    path: "/company/:orgNo/shareholders/transactions/:id/edit",
    data: { title: "transactions.edit" },
  },
  {
    name: "admin/shareholder-register-statement",
    path: "/company/:orgNo/shareholders/statement",
    data: { title: "header.altinn" },
  },
  {
    name: "admin/shareholder-register-statement-new",
    path: "/company/:orgNo/shareholders/statement/new/:year/:tab?",
    data: { title: "header.altinn" },
  },
  {
    name: "admin/shareholder-register-statement-view",
    path: "/company/:orgNo/shareholders/statement/:id",
    data: { title: "header.altinn" },
  },
  {
    name: "admin/beneficial-owners",
    path: "/company/:orgNo/shareholders/beneficial-owners",
    data: { title: "header.beneficial-owners" },
  },
  {
    name: "admin/new-beneficial-owner",
    path: "/company/:orgNo/shareholders/beneficial-owners/new",
    data: { title: "header.beneficial-owners" },
  },
  {
    name: "admin/beneficial-owner-auth-callback",
    path: "/company/:orgNo/shareholders/beneficial-owners/submit",
    data: { title: "header.beneficial-owners" },
  },
  {
    name: "admin/beneficial-owner-details",
    path: "/company/:orgNo/shareholders/beneficial-owners/:id",
    data: { title: "header.beneficial-owners" },
  },
  {
    name: "admin/beneficial-owners-log-details",
    path: "/company/:orgNo/shareholders/beneficial-owners/log/:statementId/:beneficialOwnerId?",
    data: { title: "header.beneficial-owners" },
  },
  {
    name: "admin/project-list", // this to be removed when "project-dashboard" is activated in prod
    path: "/company/:orgNo/projects",
    data: { title: "header.dbot" },
  },
  {
    name: "admin/project",
    path: "/company/:orgNo/projects/:projectId",
    data: { title: "header.dbot" },
  },
  {
    name: "admin/board-meeting-preview",
    path: "/company/:orgNo/board-meeting-preview/:projectId",
    data: { title: "meetings.board-meeting" },
  },
  {
    name: "admin/general-meeting-preview",
    path: "/company/:orgNo/general-meeting-preview/:projectId/:tab?",
    data: { title: "meetings.general-meeting" },
  },
  {
    name: "admin/messaging",
    path: "/company/:orgNo/messaging/:tab?",
    data: { title: "header.messaging" },
  },
  {
    name: "admin/archive",
    path: "/company/:orgNo/archive/:rootFolder?/:view?",
    data: { title: "header.archive" },
  },
  {
    name: "admin/schedule",
    path: "/company/:orgNo/schedule/:view?/:year?/:month?",
    data: { title: "header.schedule" },
  },
] as const satisfies Route<DCRouteData>[];

export const superadminRoutes = [
  {
    name: "superadmin",
    path: "/super-admin-backoffice/:view?/:subView?/:thirdView?",
    data: { title: "portfolio.SUPERADMIN" },
  },
] as const satisfies Route<DCRouteData>[];

export const translationRoutes = [
  {
    name: "translation-admin",
    path: "/t",
  },
];

export const userRoutes = [
  {
    name: "root/redirect",
    path: "/",
    data: { allowShareholder: true },
  },
  {
    name: "portfolio/redirect",
    path: "/portfolio",
    data: { allowShareholder: true },
  },
  {
    name: "portfolio/companies-redirect",
    path: "/portfolio/companies",
    redirectTo: "/portfolio/all/companies",
  },
  {
    name: "help-center",
    path: "/help-center",
    data: { title: "header.help-center" },
  },
  {
    name: "portfolio/help-center",
    path: "/portfolio/:portfolio/help-center",
    data: { title: "header.help-center" },
  },
  {
    name: "portfolio/search",
    path: "/portfolio/search",
    data: { title: "search" },
  },
  {
    name: "portfolio/reports",
    path: "/portfolio/reports",
    data: { title: "header.reports" },
  },
  {
    name: "portfolio/reports/result",
    path: "/portfolio/reports/result",
    data: { title: "header.reports" },
  },
  {
    name: "portfolio/companies",
    path: "/portfolio/:portfolio/companies/:tab?",
    data: { allowShareholder: true, title: "header.companies" },
  },
  {
    name: "portfolio/follow-up-signatures",
    path: "/portfolio/:portfolio/follow-up/signatures/:tab?",
    data: { allowShareholder: true, title: "header.follow-up" },
  },
  {
    name: "portfolio/follow-up-matters",
    path: "/portfolio/:portfolio/follow-up/matters/:tab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "portfolio/follow-up-company-info",
    path: "/portfolio/:portfolio/follow-up/company-info/:tab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "portfolio/follow-up-shareholders",
    path: "/portfolio/:portfolio/follow-up/shareholders/:tab?",
    data: { title: "header.follow-up" },
  },
  {
    name: "portfolio/contacts-board-members-redirect",
    path: "/portfolio/board_member/contacts/:tab?",
    redirectTo: "/portfolio/board_member/companies",
  },
  {
    name: "portfolio/contacts-board-members-custom-role-redirect",
    path: "/portfolio/board_member/contacts/custom-role/:tab?",
    redirectTo: "/portfolio/board_member/companies",
  },
  {
    name: "portfolio/contacts-shareholder-redirect",
    path: "/portfolio/shareholder/contacts/:tab?",
    redirectTo: "/portfolio/shareholder/companies",
  },
  {
    name: "portfolio/contacts-shareholder-custom-role-redirect",
    path: "/portfolio/shareholder/contacts/custom-role/:tab?",
    redirectTo: "/portfolio/shareholder/companies",
  },
  {
    name: "portfolio/contacts",
    path: "/portfolio/:portfolio/contacts/:tab?",
    data: { title: "header.contacts" },
  },
  {
    name: "portfolio/aggregated-companyrole-details",
    path: "/portfolio/:portfolio/contacts/board/:companyroleId",
    data: { title: "header.contacts" },
  },
  {
    name: "portfolio/aggregated-shareholder-details",
    path: "/portfolio/:portfolio/contacts/shareholders/:shareholderId",
    data: { title: "shareholder" },
  },
  {
    name: "portfolio/shareholder-redirect",
    path: "/portfolio/:portfolio/shareholder/:shareholderId",
    redirectTo: "/portfolio/:portfolio/shareholder/:shareholderId/overview",
  },
  {
    name: "portfolio/shareholder",
    path: "/portfolio/:portfolio/shareholder/:shareholderId/:activeTab",
    data: { title: "shareholder" },
  },
  {
    name: "portfolio/contacts-admins",
    path: "/portfolio/:portfolio/contacts/admins/:externalId",
    data: { title: "administrators" },
  },
  {
    name: "portfolio/contacts-custom-role",
    path: "/portfolio/:portfolio/contacts/custom-role/:customRoleId",
    data: { title: "header.contacts" },
  },
  {
    name: "portfolio/custom-role-details",
    path: "/portfolio/:portfolio/contacts/custom-role/:customRoleId/details/:externalId",
    data: { title: "portfolioContacts.custom-roles.details" },
  },
  {
    name: "portfolio/signatures-redirect",
    path: "/portfolio/:portfolio/signatures/:tab?",
    redirectTo: "/portfolio/:portfolio/follow-up/signatures/:tab?",
  },
  {
    name: "invitation-request",
    path: "/invitation-request/:invitationRequestId",
    data: { title: "header.invitation-request" },
  },
  {
    name: "batch-invitation-request",
    path: "/batch-invitation-request/:batchId",
    data: { title: "header.invitation-request" },
  },
  {
    name: "user/settings-redirect",
    path: "/user/settings",
    redirectTo: "/user/settings/account",
  },
  {
    name: "user/settings",
    path: "/user/settings/:tab",
    data: { allowShareholder: true, title: "user-settings" },
  },
  {
    name: "signing",
    path: "/signing/:signingMethod",
    data: { allowShareholder: true, title: "header.signatures" },
  },
  {
    name: "notice-for-shareholder",
    path: "/notice-general-meeting/resolve/:documentId",
    data: { allowShareholder: true, title: "document-type.notice_general_meeting" },
  },
  {
    name: "enterprise-account",
    path: "/enterprise-account/:id/:tab?/:accountSettingsTab?/:templateFilter?",
    data: { title: "terms-of-service.enterprise-account.label" },
  },
] as const satisfies Route<DCRouteData>[];

export const anonymousRoutes = [
  {
    name: "login",
    path: "/login",
    data: { allowAnonymous: true, title: "landing.login" },
  },
  {
    name: "login-sweden",
    path: "/login-sweden",
    data: { allowAnonymous: true, title: "landing.login" },
  },
  {
    name: "login-callback",
    path: "/login-callback",
    data: { allowAnonymous: true, title: "landing.login" },
  },
  {
    name: "sso-account-selection",
    path: "/sso-account-selection",
    data: { allowAnonymous: true, title: "landing.login" },
  },
  {
    name: "signing-completed-bankid-callback",
    path: "/signing-completed-bankid-callback",
    data: { allowAnonymous: true, title: "signing-complete" },
  },
  {
    name: "signing-complete",
    path: "/signing-complete",
    data: { allowAnonymous: true, title: "signing-complete" },
  },
  {
    name: "forgot-password",
    path: "/forgot-password",
    data: { allowAnonymous: true, title: "reset-password.title" },
  },
  {
    name: "sign",
    path: "/sign",
    data: { allowAnonymous: true, title: "signing.sign" },
  },
  {
    name: "subscription-form",
    path: "/subscription-form",
    data: { allowAnonymous: true, title: "document-type.subscription_form" },
  },
  {
    name: "notice-board-meeting",
    path: "/notice-board-meeting/:noticeDocumentId",
    data: { allowAnonymous: true, title: "document-type.notice_board_meeting" },
  },
  {
    name: "board-minutes-review/matter",
    path: "/board-minutes-review/:meetingEmailId/matter/:matterId?",
    data: { allowAnonymous: true, title: "document-type.board_meeting" },
  },
  {
    name: "board-minutes-review",
    path: "/board-minutes-review/:meetingEmailId",
    data: { allowAnonymous: true, title: "document-type.board_meeting" },
  },
  {
    name: "notice-genera-meeting/poa-recipient",
    path: "/notice-general-meeting-poa-recipient/:powerOfAttorneyRecipientId/:tab?/:matter?",
    data: { allowAnonymous: true, title: "document-type.notice_general_meeting" },
  },
  {
    name: "notice-general-meeting",
    path: "/notice-general-meeting/:noticeToGeneralMeetingId/:tab?/:matter?",
    data: { allowAnonymous: true, title: "document-type.notice_general_meeting" },
  },
  {
    name: "create-password",
    path: "/user/create-password",
    data: { allowAnonymous: true, title: "user-settings.create-password" },
  },
  {
    name: "invitation",
    path: "/invitation/:invitationId/:invitationToken",
    data: { allowAnonymous: true, title: "page-title.invitation" },
  },
  {
    name: "batch-invitation",
    path: "/batch-invitation/:batchInvitationId/:invitationToken",
    data: { allowAnonymous: true, title: "page-title.invitation" },
  },
  {
    name: "affiliate",
    path: "/affiliate/:affiliateKey",
    data: { allowAnonymous: true, title: "header.become-customer" },
  },
  {
    name: "document-for-viewing",
    path: "/document/view/:documentId",
    data: { allowAnonymous: true, title: "document" },
  },
] as const satisfies Route<DCRouteData>[];

export const routes = [
  ...boardMemberRoutes,
  ...shareholderRoutes,
  ...adminRoutes,
  ...superadminRoutes,
  ...translationRoutes,
  ...userRoutes,
  ...anonymousRoutes,
] as const satisfies Route<DCRouteData>[];

export type BoardMemberRoute = (typeof boardMemberRoutes)[number];
export type ShareholderRoute = (typeof shareholderRoutes)[number];
export type AdminRoute = (typeof adminRoutes)[number];
export type SuperadminRoute = (typeof superadminRoutes)[number];
export type UserRoute = (typeof userRoutes)[number];
export type AnonymousRoute = (typeof anonymousRoutes)[number];
export type DCRoute = (typeof routes)[number] & { data?: DCRouteData };

export type MatchedDCRoute<R extends DCRoute = DCRoute> = MatchedRoute<DCRouteData, R>;

export const isBoardMemberRoute = (
  route: MatchedDCRoute | undefined,
): route is MatchedDCRoute<BoardMemberRoute> => route?.name.startsWith("board-member/") === true;

export const isShareholderRoute = (
  route: MatchedDCRoute | undefined,
): route is MatchedDCRoute<ShareholderRoute> => route?.name.startsWith("shareholder/") === true;

export const isAdminRoute = (
  route: MatchedDCRoute | undefined,
): route is MatchedDCRoute<AdminRoute> => route?.name.startsWith("admin/") === true;

export const isSuperadminRoute = (
  route: MatchedDCRoute | undefined,
): route is MatchedDCRoute<SuperadminRoute> => route?.name.startsWith("superadmin") === true;

export const isUserRoute = (
  route: MatchedDCRoute | undefined,
): route is MatchedDCRoute<UserRoute> => userRoutes.some((r) => r.name === route?.name);

export const isAnonymousRoute = (
  route: MatchedDCRoute | undefined,
): route is MatchedDCRoute<AnonymousRoute> => anonymousRoutes.some((r) => r.name === route?.name);
